// IngredientAssistant.js
import React, { useEffect, useState, useRef } from "react";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  TextField,
  IconButton,
  Paper,
  Button,
  Link,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { fetchDrinkNotes } from "../utils/drinkUtils";

const API = process.env.REACT_APP_API_URL || "";

/* session helpers */
const load = (k, d) => JSON.parse(sessionStorage.getItem(k) || d);
const save = (k, v) => sessionStorage.setItem(k, JSON.stringify(v));
const csrftoken = () =>
  (document.cookie.match(/csrftoken=([^;]+)/) || [])[1] || null;

function IngredientAssistant() {
  const [messages, setMessages] = useState(() => load("ia_msgs", "[]"));
  const [lastId, setLastId] = useState(() => sessionStorage.getItem("ia_last") || null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [drinks, setDrinks] = useState([]);
  const [token, setToken] = useState(null);
  const chatEndRef = useRef(null);            
  useEffect(() => chatEndRef.current?.scrollIntoView({behavior:'smooth'}), [messages]);

  const nav = useNavigate();
  const loc = useLocation();

  /* persist chat locally */
  useEffect(() => save("ia_msgs", messages), [messages]);

  /* auth token */
  useEffect(() => {
    fetch(`${API}/api/get-token/`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setToken(d.token))
      .catch((e) => console.error("token", e));
  }, []);

  /* image from Home → assistant */
  useEffect(() => {
    const img = loc.state?.imageFile;
    if (img && token) resize(img).then((f) => chat({ imageFile: f }));
  }, [loc.state, token]);

  /* -------------- helpers ---------------- */
  const resize = (file, maxW = 1024, q = 0.7) =>
    new Promise((res, rej) => {
      const img = new Image();
      const fr = new FileReader();
      fr.onload = (e) => (img.src = e.target.result);
      fr.onerror = rej;
      img.onload = () => {
        const s = maxW / img.width,
          c = Object.assign(document.createElement("canvas"), {
            width: maxW,
            height: img.height * s,
          });
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        c.toBlob(
          (b) =>
            b
              ? res(new File([b], "resized.jpg", { type: "image/jpeg" }))
              : rej(new Error("resize")),
          "image/jpeg",
          q
        );
      };
      fr.readAsDataURL(file);
    });

  /* main chat fn */
  const chat = async ({ message = null, imageFile = null }) => {
    setBusy(true);
    try {
      /* ---------- build fetch options ---------- */
      const opts =
        imageFile === null
          ? {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ message, previous_response_id: lastId }),
            }
          : {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: (() => {
                const fd = new FormData();
                fd.append("image", imageFile);
                if (lastId) fd.append("previous_response_id", lastId);
                return fd;
              })(),
            };

      const r = await fetch(`${API}/api/ingredient-chat/`, opts);

      /* ----- async image path: 202 + task_id ----- */
      if (r.status === 202) {
        const { task_id } = await r.json();

        /* show temporary placeholder bubble */
        setMessages((p) => [
          ...p,
          { placeholder: true, role: "assistant", content: "Let me see…" },
        ]);

        taskLoop(task_id);
        return; // don't fall through; wait for polling
      }

      /* ----- normal synchronous path ----- */
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();

      setMessages((p) => [...p, ...data.output]);

      if (data.response_id) {
        setLastId(data.response_id);
        sessionStorage.setItem("ia_last", data.response_id);
      }

      if (data.drink_ids?.length) await loadDrinks(data.drink_ids);
    } catch (e) {
      console.error("chat", e);
    } finally {
      setBusy(false);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    resize(file).then((f) => {
      /* 1 ▸ preview bubble that persists */
      const url = URL.createObjectURL(file);
      setMessages((p) => [...p, { role: "user", image: url }]);
      /* 2 ▸ fire chat */
      chat({ imageFile: f });
    });
  };

  const loadDrinks = async (ids) => {
    const r = await fetch(`${API}/api/carousel-drink-details/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken(),
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify({ ids }),
    });
    const { drinks = [] } = await r.json();
    const noteMap = await fetchDrinkNotes(ids);
    setDrinks(drinks.map((d) => ({ ...d, notes: noteMap.get(d.id) || "" })));
  };

  /* submit from textbox */
  const sendText = () => {
    if (!input.trim()) return;
    setMessages((p) => [...p, { role: "user", content: input }]);
    chat({ message: input });
    setInput("");
  };

  const taskLoop = (id) => {
    const timer = setInterval(async () => {
      const r = await fetch(`${API}/api/chat-result/${id}`);
      if (r.status === 200) {
        clearInterval(timer);
        const data = await r.json();

        /* 1 ▸ replace the placeholder bubble */
        setMessages((prev) =>
          prev.map((m) =>
            m.placeholder ? { role: "assistant", content: data.assistant_text } : m
          )
        );

        /* 2 ▸ load carousel */
        if (data.drink_ids?.length) loadDrinks(data.drink_ids);

        /* 3 ▸ remember response_id for threading */
        if (data.response_id) {
          setLastId(data.response_id);
          sessionStorage.setItem("ia_last", data.response_id);
        }
      }
    }, 1500);
  };

  const groupDrinksByName = (drinks) => {
    const map = new Map();
    for (const drink of drinks) {
      const key = drink.name;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(drink);
    }
    // sort each group by version_number
    for (const group of map.values()) {
      group.sort((a, b) => a.version_number - b.version_number);
    }
    return Array.from(map.values());
  };

  /* ---------------------- UI ---------------------- */
  return (
    <Container sx={{ py: 2 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Ingredient Assistant
      </Typography>

      <Box
        sx={{
          maxHeight: "50vh",
          overflowY: "auto",
          mb: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.map((m, i) => (
          <Paper
            key={i}
            sx={{
              p: 1,
              mb: 1,
              maxWidth: '80%',
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              backgroundColor: m.role === 'user' ? 'primary.main' : 'grey.800',
              color: '#fff',
            }}
          >
            {m.image ? (
              <img src={m.image} alt="upload" style={{ maxWidth: '100%' }} />
            ) : (
              m.content
            )}
          </Paper>
        ))}
        {busy && <CircularProgress size={24} sx={{ mt: 2 }} />}
      <div ref={chatEndRef} />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
         <input
           type="file"
           accept="image/*"
           id="ia-file"
           style={{ display: "none" }}
           onChange={handleImagePick}
         />
        <IconButton component="label" htmlFor="ia-file" disabled={busy}>
          <AddPhotoAlternateIcon />
        </IconButton>
        <TextField
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message…"
        />
         <IconButton onClick={sendText} disabled={busy}>
           <SendIcon />
         </IconButton>
       </Box>

      {drinks.length > 0 && (
        <Box sx={{ mt: 4, display: "flex", overflowX: "auto", gap: 2, pb: 2 }}>
          {groupDrinksByName(drinks).map((versions) => {
            const v1 = versions[0];
            return (
              <Link
                component={RouterLink}
                to={`/drinks/${v1.id}`}
                key={v1.id}
                underline="none"
                sx={{
                  flex: "0 0 auto",
                  width: "clamp(160px, 80vw, 250px)",
                  height: "clamp(240px, 60vh, 400px)",
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "grey.900",
                    borderRadius: 2,
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h6" align="center" sx={{ color: "#fff" }}>
                    {v1.name}
                  </Typography>
                  {versions.length > 1 && (
                    <Typography
                      variant="caption"
                      align="center"
                      sx={{ color: "orange", mt: 0.5 }}
                    >
                      {versions.length} versions
                    </Typography>
                  )}
                  {v1.notes && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        fontStyle: "italic",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 10,
                        WebkitBoxOrient: "vertical",
                        whiteSpace: "normal",
                      }}
                    >
                      {v1.notes}
                    </Typography>
                  )}
                </Paper>
              </Link>
            );
          })}
        </Box>
      )}


      <Box mt={4}>
        <Button fullWidth variant="outlined" onClick={() => nav("/")}>
          Back to Home
        </Button>
      </Box>
    </Container>
  );
}

export default IngredientAssistant;
